import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusCircle } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

export interface FieldDefinition {
  key: string;
  label: string;
  type: "text" | "email" | "tel" | "url" | "number" | "select" | "textarea" | "date";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  onAddOption?: (newValue: string) => Promise<boolean>;
}

interface EditRowDialogProps<T extends Record<string, unknown>> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  fields: FieldDefinition[];
  data: T | null;
  onSave: (data: T) => Promise<void> | void;
}

export function EditRowDialog<T extends Record<string, unknown>>({
  open,
  onOpenChange,
  title = "Edit",
  description,
  fields,
  data,
  onSave,
}: EditRowDialogProps<T>) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addingNewField, setAddingNewField] = useState<string | null>(null);
  const [newOptionValue, setNewOptionValue] = useState("");

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open && data) {
      setFormData({ ...data });
      setErrors({});
    }
  }, [open, data]);

  const handleFieldChange = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    // Clear error when field is modified
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    for (const field of fields) {
      const value = formData[field.key];
      const isEmpty = value === undefined || value === null || value === "";

      // Required check
      if (field.required && isEmpty) {
        newErrors[field.key] = `${field.label} is required`;
        continue;
      }

      // Skip type validation if empty and not required
      if (isEmpty) continue;

      // Type-specific validation
      switch (field.type) {
        case "number": {
          const num = Number(value);
          if (isNaN(num)) {
            newErrors[field.key] = `${field.label} must be a valid number`;
          } else if (num < 0) {
            newErrors[field.key] = `${field.label} cannot be negative`;
          }
          break;
        }
        case "date": {
          const dateStr = String(value);
          const parsed = new Date(dateStr);
          if (isNaN(parsed.getTime())) {
            newErrors[field.key] = `${field.label} must be a valid date`;
          }
          break;
        }
        case "email": {
          const emailStr = String(value).trim();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr)) {
            newErrors[field.key] = `${field.label} must be a valid email`;
          }
          break;
        }
        case "url": {
          const urlStr = String(value).trim();
          try {
            new URL(urlStr);
          } catch {
            newErrors[field.key] = `${field.label} must be a valid URL`;
          }
          break;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      await onSave(formData as T);
      onOpenChange(false);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderField = (field: FieldDefinition) => {
    const value = formData[field.key];
    const error = errors[field.key];
    const controlClassName = error
      ? "border-destructive bg-input/70 text-foreground"
      : "border-border bg-input/70 text-foreground";

    switch (field.type) {
      case "select":
        if (addingNewField === field.key) {
          return (
            <div className="flex gap-2 items-center">
              <Input
                value={newOptionValue}
                onChange={(e) => setNewOptionValue(e.target.value)}
                placeholder={`New ${field.label.toLowerCase()}`}
                className="flex-1 border-border bg-input/70 text-foreground"
                autoFocus
                onKeyDown={async (e) => {
                  if (e.key === "Enter" && newOptionValue.trim() && field.onAddOption) {
                    const ok = await field.onAddOption(newOptionValue.trim());
                    if (ok) {
                      handleFieldChange(field.key, newOptionValue.trim());
                      setAddingNewField(null);
                      setNewOptionValue("");
                    }
                  }
                  if (e.key === "Escape") {
                    setAddingNewField(null);
                    setNewOptionValue("");
                  }
                }}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={!newOptionValue.trim()}
                onClick={async () => {
                  if (newOptionValue.trim() && field.onAddOption) {
                    const ok = await field.onAddOption(newOptionValue.trim());
                    if (ok) {
                      handleFieldChange(field.key, newOptionValue.trim());
                      setAddingNewField(null);
                      setNewOptionValue("");
                    }
                  }
                }}
              >
                Add
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setAddingNewField(null); setNewOptionValue(""); }}>
                Cancel
              </Button>
            </div>
          );
        }
        return (
          <Select
            value={value as string || ""}
            onValueChange={(v) => {
              if (v === "__ADD_NEW__") {
                setAddingNewField(field.key);
                setNewOptionValue("");
              } else {
                handleFieldChange(field.key, v);
              }
            }}
          >
            <SelectTrigger className={controlClassName}>
              <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
              {field.onAddOption && (
                <>
                  <SelectSeparator />
                  <SelectItem value="__ADD_NEW__">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <PlusCircle size={13} /> Add New {field.label}
                    </span>
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        );

      case "textarea":
        return (
          <textarea
            value={(value as string) || ""}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={`flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 ${
              error ? "border-destructive bg-input/70 text-foreground" : "border-border bg-input/70 text-foreground"
            }`}
          />
        );

      case "date":
        return (
          <Input
            type="date"
            value={(value as string) || ""}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className={controlClassName}
            style={{ colorScheme: theme === "light" ? "light" : "dark" }}
          />
        );

      default:
        // For number fields, don't treat 0 as falsy - only use empty string for null/undefined
        const displayValue = field.type === "number"
          ? (value !== null && value !== undefined ? String(value) : "")
          : ((value as string) || "");
        return (
          <Input
            type={field.type}
            value={displayValue}
            onChange={(e) => handleFieldChange(field.key, field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
            placeholder={field.placeholder}
            className={controlClassName}
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto border-border bg-card text-foreground">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {fields.map((field) => (
            <div key={field.key} className="grid gap-2">
              <Label htmlFor={field.key}>
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {renderField(field)}
              {errors[field.key] && (
                <p className="text-sm text-destructive">{errors[field.key]}</p>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
